import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWatiSocket } from '../components/contexts/WatiSocketContext';
import { useWatiChat } from '../components/contexts/WatiChatContext';
import { useAuth } from '../components/contexts/AuthContext';
import whatsapp_placeholder from '../assets/images/whatsapp_placeholder.png';
import useSnackbar from "../components/Snackbar/UseSnackbar";
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { API_DOMAIN } from '../components/constants';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import Avatar from '@mui/material/Avatar';
import { Stack } from '@mui/material';
import { Badge, Button, Card, Divider, Image, Input, Modal, Select, Tabs, Tooltip } from 'antd';
import { useCommonApiCall } from '../components/Api/commonApiCall';
import person_avatar from '../assets/person_avatar.svg'
import loading_fountain from '../assets/images/Fountain.gif'
import loading_circle_eclipse from '../assets/images/tube-spinner.svg'
import { DeleteOutlined, DownloadOutlined, EditOutlined, FileOutlined, FilterOutlined, LoadingOutlined, PlusCircleOutlined, SendOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';

function WhatsappMessages() {

    const { watiChatUpdates, isWatiConnected, reconnect } = useWatiSocket();
    const { chatlist, setChatlist, chatlistLoading, activeChatIdRef, totalUnread, getAllChats } = useWatiChat();
    const { authState } = useAuth();

    const { CommonApiCall } = useCommonApiCall()

    const { SuccessSnackbar, ErrorSnackbar, InfoSnackbar, WarningSnackbar } = useSnackbar();
    const [chatWindowLoading, setChatWindowLoading] = useState(true);
    const [selectedChat, setSelectedChat] = useState(null);
    const [selectedChatExchanges, setSelectedChatExchanges] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [loadingOldChats, setLoadingOldChats] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchFilter, setSearchFilter] = useState(0);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);

    const search_chats = async (query = searchQuery, filter = searchFilter) => {
        if (!query.trim()) { getAllChats(); return; }
        setSearchLoading(true);
        const response = await CommonApiCall(
            { search: query, search_filter: filter },
            'wati_messages/fetch_messages',
            'post'
        );
        if (response.status) {
            setChatlist(response.data.final_data ?? response.data);
        }
        setSearchLoading(false);
    };

    const displayedChatlist = activeTab === 'unread' ? chatlist.filter(c => c.unreadCount > 0) : chatlist;

    const [messageText, setMessageText] = useState("");
    const [isSending, setIsSending] = useState(false);

    const scrollBottomRef = useRef(null);
    const messageContainerRef = useRef(null);
    const messageInputRef = useRef(null);
    const watiProcessedCountRef = useRef(0);
    const selectedChatRef = useRef(null);
    const wasDisconnectedRef = useRef(false);
    const scrollRestoreRef = useRef(null);
    const messageRefsMap = useRef({});
    // Maps localMessageId (from send API) -> tempId so the socket echo can replace the optimistic bubble
    const pendingTempMap = useRef(new Map());
    const [flashingMessageId, setFlashingMessageId] = useState(null);

    // Edit contact modal
    const [editContactModal, setEditContactModal] = useState(false);
    const [editContactName, setEditContactName] = useState('');
    const [editContactLoading, setEditContactLoading] = useState(false);

    // Delete chat modal
    const [deleteContactModal, setDeleteContactModal] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');
    const [deleteContactLoading, setDeleteContactLoading] = useState(false);

    const openEditModal = () => {
        const chat = chatlist.find(c => c.id === selectedChat);
        setEditContactName(chat?.name || '');
        setEditContactModal(true);
    };

    const handleEditContact = async () => {
        if (!editContactName.trim()) {
            WarningSnackbar('Enter a valid name');
            return;
        }
        setEditContactLoading(true);
        try {
            const response = await CommonApiCall(
                { chatId: selectedChat, name: editContactName.trim() },
                'wati_messages/update_contact_name',
                'post'
            );
            if (response.status) {
                setChatlist(prev => prev.map(c => c.id === selectedChat ? { ...c, name: editContactName.trim() } : c));
                SuccessSnackbar('Contact name updated!');
                setEditContactModal(false);
            } else {
                ErrorSnackbar(response.message || 'Failed to update contact name');
            }
        } catch (error) {
            ErrorSnackbar(error.message);
        } finally {
            setEditContactLoading(false);
        }
    };

    const openDeleteModal = () => {
        setDeleteReason('');
        setDeleteContactModal(true);
    };

    const handleDeleteContact = async () => {
        if (!deleteReason.trim()) {
            WarningSnackbar('Please enter a reason');
            return;
        }
        setDeleteContactLoading(true);
        try {
            const response = await CommonApiCall(
                { chatId: selectedChat, reason: deleteReason.trim() },
                'wati_messages/delete_contact',
                'post'
            );
            if (response.status) {
                setChatlist(prev => prev.filter(c => c.id !== selectedChat));
                setSelectedChat(null);
                activeChatIdRef.current = null;
                setSelectedChatExchanges([]);
                SuccessSnackbar('Contact deleted!');
                setDeleteContactModal(false);
            } else {
                ErrorSnackbar(response.message || 'Failed to delete contact');
            }
        } catch (error) {
            ErrorSnackbar(error.message);
        } finally {
            setDeleteContactLoading(false);
        }
    };

    // Keep selectedChatRef in sync so the socket handler always sees the latest value
    useEffect(() => {
        selectedChatRef.current = selectedChat;
    }, [selectedChat]);

    // After prepending old messages, restore the scroll position so the view doesn't jump
    useLayoutEffect(() => {
        if (scrollRestoreRef.current !== null && messageContainerRef.current) {
            const newScrollHeight = messageContainerRef.current.scrollHeight;
            messageContainerRef.current.scrollTop = newScrollHeight - scrollRestoreRef.current;
            scrollRestoreRef.current = null;
        }
    }, [selectedChatExchanges]);

    // Independent listener for incoming Wati socket events
    useEffect(() => {
        if (watiChatUpdates.length <= watiProcessedCountRef.current) return;

        const newUpdates = watiChatUpdates.slice(watiProcessedCountRef.current);
        watiProcessedCountRef.current = watiChatUpdates.length;

        newUpdates.forEach(update => {
            if (update.target === 'sendTextMessage' && update['0']?.message?.conViewModel?.channelId === '68d251023d3de3334068a1d9') {
                const conversationId = update['0'].conversationId;

                if (selectedChatRef.current === conversationId) {
                    const isMedia = update['0'].messageType === 'image' || update['0'].messageType === 'video' || update['0'].messageType === 'audio';
                    const isDocument = update['0'].messageType === 'document';
                    const newMsg = {
                        id: update['0'].messageId,
                        type: update['0'].messageType,
                        text: update['0'].message.text,
                        owner: update['0'].message.isOwner,
                        statusString: update['0'].message.statusString,
                        timestamp: parseInt(update['0'].timestamp),
                        replySourceMessage: update['0'].replySourceMessage,
                        ...(isMedia && { data: null, data_original: update['0'].message.data }),
                        ...(isDocument && { data_original: update['0'].message.data }),
                    };
                    setSelectedChatExchanges(prev => {
                        const localMsgId = update['0'].localMessageId;
                        const tempId = localMsgId ? pendingTempMap.current.get(localMsgId) : undefined;
                        if (tempId) {
                            pendingTempMap.current.delete(localMsgId);
                            const tempIdx = prev.findIndex(m => m.id === tempId);
                            if (tempIdx !== -1) {
                                const next = [...prev];
                                next[tempIdx] = newMsg;
                                return next;
                            }
                        }
                        // Guard against duplicates (e.g. already in list)
                        if (prev.some(m => m.id === newMsg.id)) return prev;
                        return [...prev, newMsg];
                    });
                    if (isMedia && update['0'].message.data) {
                        handleMediaNeedLoad(update['0'].messageId, update['0'].message.data);
                    }
                    setTimeout(() => {
                        if (messageContainerRef.current) {
                            const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
                            if (scrollHeight - scrollTop - clientHeight < 300) {
                                scrollToBottom("smooth");
                            }
                        }
                    }, 50);
                    CommonApiCall({ chatId: conversationId }, "wati_messages/mark_chat_as_read", "post");
                }
            }

            // Update read receipt / delivery status on an already-rendered message
            if (update.target === 'updatingOperatorMessageStatus') {
                const msgData = Array.isArray(update['0']) ? update['0'][0] : update['0'];
                if (msgData && selectedChatRef.current === msgData.conversationId) {
                    setSelectedChatExchanges(prev =>
                        prev.map(msg => msg.id === msgData.id ? { ...msg, statusString: msgData.statusString } : msg)
                    );
                }
            }

            // Operator message sent from this session or another — always owner:true
            if (update.target === 'messageFromOperator') {
                const msg = update['0'];
                if (selectedChatRef.current === msg?.conversationId) {
                    const isMedia = msg.type === 'image' || msg.type === 'video' || msg.type === 'audio';
                    const isDocument = msg.type === 'document';
                    const newMsg = {
                        id: msg.id,
                        localMessageId: msg.localMessageId,
                        conversationId: msg.conversationId,
                        type: msg.type,
                        text: msg.text,
                        time: msg.time,
                        owner: true,
                        isOwner: true,
                        statusString: msg.statusString,
                        timestamp: parseInt(msg.time),
                        operatorName: msg.operatorName,
                        media: msg.media,
                        replyContextId: msg.replyContextId,
                        replySourceMessage: msg.replyContextId || null,
                        ...(isMedia && { data: null, data_original: msg.media }),
                        ...(isDocument && { data_original: msg.media }),
                    };
                    setSelectedChatExchanges(prev => {
                        // If this is our own sent message echo, replace the temp bubble
                        const localMsgId = msg.localMessageId;
                        const tempId = localMsgId ? pendingTempMap.current.get(localMsgId) : undefined;
                        if (tempId) {
                            pendingTempMap.current.delete(localMsgId);
                            const tempIdx = prev.findIndex(m => m.id === tempId);
                            if (tempIdx !== -1) {
                                const next = [...prev];
                                next[tempIdx] = newMsg;
                                return next;
                            }
                        }
                        // Guard against duplicates (e.g. already replaced or re-delivered)
                        if (prev.some(m => m.id === newMsg.id)) return prev;
                        return [...prev, newMsg];
                    });
                    setTimeout(() => {
                        if (messageContainerRef.current) {
                            const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
                            if (scrollHeight - scrollTop - clientHeight < 300) {
                                scrollToBottom("smooth");
                            }
                        }
                    }, 50);
                }
            }
        });
    }, [watiChatUpdates]);

    const scrollToBottom = (behavior = "auto") => {
        if (scrollBottomRef.current) {
            scrollBottomRef.current.scrollIntoView({ behavior });
        }
    };

    const scrollToMessage = (messageId) => {
        const el = messageRefsMap.current[messageId];
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setFlashingMessageId(messageId);
        setTimeout(() => setFlashingMessageId(null), 1500);
    };
    useEffect(() => {
        if (!chatWindowLoading) {
            setTimeout(() => scrollToBottom("auto"), 100); // Small timeout ensures DOM has rendered
            setTimeout(() => messageInputRef.current?.focus(), 150);
        }
    }, [chatWindowLoading]);

    // Track disconnects so we only show the snackbar on re-connection, not on first connect
    useEffect(() => {
        if (!isWatiConnected) {
            wasDisconnectedRef.current = true;
        } else if (wasDisconnectedRef.current) {
            wasDisconnectedRef.current = false;
            SuccessSnackbar('Socket reconnected successfully.');
        }
    }, [isWatiConnected]);

    const handleMediaLoad = () => {
        if (messageContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;

            // If the user is within 300px of the bottom, they are likely
            // looking at new messages, so we keep them snapped to the bottom.
            // If they are further up, we do NOTHING so they don't lose their place.
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 300;

            if (isNearBottom) {
                scrollToBottom("auto");
            }
        }
    };

    function stringToColor(string) {
        let hash = 0;
        let i;

        /* eslint-disable no-bitwise */
        for (i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = '#';

        for (i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        /* eslint-enable no-bitwise */

        return color;
    }

    function stringAvatar(name) {
        if (!name) {
            return "";
        }
        if (name.split(' ').length === 2) {
            // If name has two parts (first name and last name)
            return {
                sx: {
                    bgcolor: stringToColor(name),
                },
                children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
            };
        } else {
            // If name has only one part
            return {
                sx: {
                    bgcolor: stringToColor(name),
                },
                children: `${name.split(' ')[0][0].toUpperCase()}`, // Capitalize first letter
            };
        }
    }



    const getUnreadBadgeColor = (count) => {
        if (count > 10) return "#dc2626";
        if (count > 5) return "#d97706";
        return "#52c41a";
    };

    const STATUS_META = {
        SENDING: { color: '#9ca3af', icon: '⏱' },
        SENT: { color: '#6b7280', icon: '✓' },
        DELIVERED: { color: '#3b82f6', icon: '✓✓' },
        READ: { color: '#22c55e', icon: '✓✓' },
        FAILED: { color: '#ef4444', icon: '✗' },
    };

    const renderStatus = (statusString) => {
        if (!statusString) return null;
        const meta = STATUS_META[statusString.toUpperCase()] || { color: '#9ca3af', icon: '' };
        return (
            <span style={{ color: meta.color, fontWeight: 600, marginLeft: 2 }}>
                {meta.icon} {statusString}
            </span>
        );
    };

    const formatChatMessageTime = (epoch) => {
        if (!epoch)
            return null
        const date = new Date(epoch * 1000);
        const now = new Date();

        // Check if it's today by comparing Year, Month, and Date
        const isToday =
            date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();

        const timeOptions = {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        };

        if (isToday) {
            return new Intl.DateTimeFormat('default', timeOptions).format(date);
        } else {
            // For older messages, include the date (e.g., "Feb 15, 12:02 PM")
            return new Intl.DateTimeFormat('default', {
                month: 'short',
                day: 'numeric',
                ...timeOptions
            }).format(date);
        }
    };

    const set_selected_chat_state = async (chatId) => {
        if (chatId === selectedChatRef.current) return;
        setSelectedChat(chatId);
        activeChatIdRef.current = chatId;
        setChatWindowLoading(true);
        setHasMoreMessages(false);
        try {
            const response = await CommonApiCall({
                chatId,
                lastMessageId: null
            }, "wati_messages/fetch_specific_chat_messages", "post");
            if (response.status) {
                console.log(`Fetched messages for chat ${chatId}:`, JSON.stringify(response.data));

                const exchanges = response.data.data.reverse().map(msg => {
                    if (msg.type === 'image' || msg.type === 'video' || msg.type === 'audio')
                        return { ...msg, data_original: msg.data_original || msg.data, data: null };
                    if (msg.type === 'document')
                        return { ...msg, data_original: msg.data_original || msg.data };
                    return msg;
                });
                setSelectedChatExchanges(exchanges);
                setHasMoreMessages(exchanges.length > 0);
                [...exchanges].reverse().forEach(msg => {
                    if ((msg.type === 'image' || msg.type === 'video' || msg.type === 'audio') && msg.data_original) {
                        handleMediaNeedLoad(msg.id, msg.data_original);
                    }
                });
                setChatlist(prev => prev.map(chat =>
                    chat.id === chatId ? { ...chat, unreadCount: 0 } : chat
                ));
            }
        } catch (error) {
            ErrorSnackbar(error.message);
        }
        finally {
            setChatWindowLoading(false);
            //mark chat as read on backend so that unread count is consistent across clients
            await CommonApiCall({
                chatId
            }, "wati_messages/mark_chat_as_read", "post");
        }
    }

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setSelectedChat(null);
                activeChatIdRef.current = null;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);
    const formatWhatsAppText = (text) => {
        if (!text)
            return null;
        // Regex to capture: *bold*, _italic_, ~strikethrough~, and ```monospace```
        const regex = /(\*.*?\*|__.*?__|~.*?~|```.*?```)/g;

        const parts = text.split(regex);

        return parts.map((part, index) => {
            if (!part) return null;

            // Monospace: ```text```
            if (part.startsWith('```') && part.endsWith('```')) {
                return (
                    <code key={index} className="bg-gray-100 p-0.5 rounded font-mono text-xs">
                        {part.slice(3, -3)}
                    </code>
                );
            }
            // Bold: *text*
            if (part.startsWith('*') && part.endsWith('*')) {
                return <strong key={index} className="font-bold">{part.slice(1, -1)}</strong>;
            }
            // Italic: __text__ or _text_
            if ((part.startsWith('__') && part.endsWith('__')) || (part.startsWith('_') && part.endsWith('_'))) {
                const content = part.startsWith('__') ? part.slice(2, -2) : part.slice(1, -1);
                return <em key={index} className="italic">{content}</em>;
            }
            // Strikethrough: ~text~
            if (part.startsWith('~') && part.endsWith('~')) {
                return <span key={index} className="line-through text-gray-400">{part.slice(1, -1)}</span>;
            }

            // Plain text
            return part;
        });
    };

    const handleDownload = async (url, fileName, chatId) => {
        if (downloadingId) {
            InfoSnackbar(`Wait till current download finish`)
            return;
        }
        setDownloadingId(chatId);
        try {
            const response = await axios.post(
                `${API_DOMAIN}/wati_messages/download_wati_media_file`,
                { file_name: url },
                { responseType: 'blob' } // This tells axios to keep the binary data intact
            );
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName || 'uzdpart_media.jpg';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download failed", error);
        } finally {
            setDownloadingId(null); // Stop loading
        }
    };
    const handleMediaNeedLoad = async (chatId, link) => {
        const response = await CommonApiCall({
            file_name: link
        }, "wati_messages/parse_media_file_url", "post");
        if (response.status) {

            setSelectedChatExchanges(prev =>
                prev.map(msg =>
                    msg.id == chatId
                        ? { ...msg, data: response.data } // Correctly updating the 'data' property
                        : msg
                )
            );
        }

        // TODO: Add your API call here
        // chatId  — the message id that needs media
        // type    — 'image' or 'video'
        // After fetching, update selectedChatExchanges:
    };

    const loadOldChats = async () => {
        if (!hasMoreMessages || loadingOldChats) return;
        const chatId = selectedChatRef.current;
        if (!chatId) return;
        setLoadingOldChats(true);
        try {
            // Use the oldest message currently displayed as the cursor
            const oldestMessageId = selectedChatExchanges[0]?.id ?? null;
            const response = await CommonApiCall({
                chatId,
                lastMessageId: oldestMessageId,
            }, "wati_messages/fetch_specific_chat_messages", "post");
            if (response.status) {
                const oldMessages = (response.data.data ?? []).reverse().map(msg => {
                    if (msg.type === 'image' || msg.type === 'video' || msg.type === 'audio')
                        return { ...msg, data_original: msg.data_original || msg.data, data: null };
                    if (msg.type === 'document')
                        return { ...msg, data_original: msg.data_original || msg.data };
                    return msg;
                });
                if (oldMessages.length === 0) {
                    setHasMoreMessages(false);
                    return;
                }
                // Capture scroll height BEFORE prepending so useLayoutEffect can restore position
                if (messageContainerRef.current) {
                    scrollRestoreRef.current = messageContainerRef.current.scrollHeight;
                }
                setSelectedChatExchanges(prev => [...oldMessages, ...prev]);
                // Kick off media loads for the newly prepended messages
                oldMessages.forEach(msg => {
                    if ((msg.type === 'image' || msg.type === 'video' || msg.type === 'audio') && msg.data_original) {
                        handleMediaNeedLoad(msg.id, msg.data_original);
                    }
                });
            }
        } catch (error) {
            ErrorSnackbar(error.message);
        } finally {
            setLoadingOldChats(false);
        }
    };

    const handleSendMessage = async () => {
        if (!messageText.trim() || isSending) return;

        const phone = chatlist.find(c => c.id === selectedChat)?.whatsappId;
        if (!phone) return;

        const sentText = messageText;
        const tempId = 'temp_' + Date.now();
        const localMessageId = uuidv4().toString();
        pendingTempMap.current.set(localMessageId, tempId);
        const tempMsg = {
            id: tempId,
            localMessageId,
            conversationId: selectedChat,
            type: 'text',
            text: sentText,
            time: String(Math.floor(Date.now() / 1000)),
            owner: true,
            isOwner: true,
            statusString: 'SENDING',
            timestamp: Math.floor(Date.now() / 1000),
            operatorName: authState.user?.name,
            media: null,
            replyContextId: null,
            replySourceMessage: null,
        };

        setSelectedChatExchanges(prev => [...prev, tempMsg]);
        setMessageText('');
        setIsSending(true);
        setTimeout(() => scrollToBottom('smooth'), 50);

        try {
            await axios.post(`https://srm.uzdpart.com:33123/whatsapp/send_wati_general_message`, {
                phone,
                message: sentText,
                user: authState.user?.name,
                channelId: '68d251023d3de3334068a1d9',
                localMessageId,
            });

            const sentAt = Math.floor(Date.now() / 1000);
            setSelectedChatExchanges(prev =>
                prev.map(msg => msg.id === tempId ? { ...msg, statusString: 'SENT' } : msg)
            );
            setChatlist(prev =>
                [...prev.map(chat =>
                    chat.id === selectedChat
                        ? { ...chat, text: sentText, lastMessageDate: sentAt, lastUpdated: String(sentAt) }
                        : chat
                )].sort((a, b) => parseFloat(b.lastUpdated) - parseFloat(a.lastUpdated))
            );
        } catch (err) {
            pendingTempMap.current.delete(localMessageId);
            setSelectedChatExchanges(prev => prev.filter(msg => msg.id !== tempId));
            setMessageText(sentText);
            ErrorSnackbar('Failed to send message. Please try again.');
        } finally {
            setIsSending(false);
        }
    };
    return (
        <>

            <div className='border rounded drop-shadow-2 p-2' style={{ backgroundColor: "#ebebeb", height: "80vh" }}>
                <div className='flex h-full'> {/* Added h-full here */}

                    {/* LEFT PANE */}
                    <div className='mr-2 border rounded flex flex-col' style={{ backgroundColor: "#cacbcc", minWidth: "320px" }}>
                        {/* Header section (Search/Counts) */}
                        <div className='flex-none'>
                            {!isWatiConnected && (
                                <div className='flex items-center justify-between px-2 py-1' style={{ backgroundColor: '#fff3cd', borderBottom: '1px solid #ffc107' }}>
                                    <span style={{ fontSize: 12, color: '#856404' }}>⚠️ Socket disconnected</span>
                                    <Button size="small" type="primary" danger onClick={reconnect}>Reconnect</Button>
                                </div>
                            )}
                            <div className='flex items-center gap-1 p-2'>
                                <Input
                                    allowClear
                                    placeholder='Search'
                                    value={searchQuery}
                                    onChange={e => {
                                        setSearchQuery(e.target.value);
                                        if (!e.target.value) getAllChats();
                                    }}
                                    onKeyDown={e => { if (e.key === 'Enter') search_chats(); }}
                                    suffix={<SearchOutlinedIcon style={{ fontSize: 16, color: '#aaa', cursor: 'pointer' }} onClick={() => search_chats()} />}
                                    style={{ borderColor: "gray" }}
                                />
                                <Tooltip title={`Filter: ${searchFilter === 0 ? 'Name/Number' : 'Messages'}`}>
                                    <Button
                                        icon={<FilterOutlined />}
                                        onClick={() => setFilterModalOpen(true)}
                                        style={searchFilter !== 0 ? { borderColor: '#1d4ed8', color: '#1d4ed8' } : {}}
                                    />
                                </Tooltip>
                            </div>
                            <Tabs
                                size="small"
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                className="px-2"
                                items={[
                                    { key: 'all', label: `All (${chatlist.length})` },
                                    {
                                        key: 'unread',
                                        label: (
                                            <Badge count={totalUnread} size="small" offset={[8, 0]}>
                                                Unread
                                            </Badge>
                                        ),
                                    },
                                ]}
                            />
                        </div>

                        {/* SCROLLABLE LIST: flex-1 makes it take all remaining space */}
                        <div className='flex-1 overflow-y-auto mt-1 p-1'>
                            {
                                (chatlistLoading || searchLoading)
                                    ?
                                    <div className='flex flex-col items-center gap-2 pt-6'>
                                        <img src={loading_fountain} />
                                        {searchLoading && <span style={{ fontSize: 12, color: '#888' }}>Searching…</span>}
                                    </div>
                                    :
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={activeTab}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -8 }}
                                            transition={{ duration: 0.18, ease: 'easeInOut' }}
                                        >
                                            {displayedChatlist.length > 0 ?
                                                <AnimatePresence>
                                                    {displayedChatlist.map((chat) => (
                                                        <motion.div
                                                            key={chat.id}
                                                            layout
                                                            initial={{ opacity: 0, y: -8 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, scale: 0.95 }}
                                                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                                                            whileHover={{ scale: 1.02 }}
                                                            whileTap={{ scale: 0.97 }}
                                                            className="m-1.5 p-1 border rounded drop-shadow cursor-pointer select-none"
                                                            style={{ width: "300px", height: "80px", backgroundColor: `${selectedChat == chat.id ? '#7bfc68' : 'white'}` }}
                                                            onClick={() => set_selected_chat_state(chat.id)}
                                                        >
                                                            <div className="flex">
                                                                <img style={{ width: "40px", height: "40px", borderRadius: "20px" }} src={person_avatar} alt="DP" loading="lazy" />
                                                                <div className="pl-1" style={{ width: "130px" }}>
                                                                    {/* name and last message preview part */}
                                                                    <div className='flex' style={{ width: "199%" }}>
                                                                        <div className="truncate w-[230px] font-semibold">{chat.name}</div>
                                                                        <motion.span
                                                                            animate={chat.unreadCount > 5 ? { scale: [1, 1.5, 1] } : {}}
                                                                            transition={{ duration: 0.55, repeat: Infinity, ease: "easeInOut" }}
                                                                        >
                                                                            <Badge className='mr-1' id={'unread' + chat.whatsappId} count={chat.unreadCount} style={{ backgroundColor: getUnreadBadgeColor(chat.unreadCount) }} />
                                                                        </motion.span>
                                                                    </div>
                                                                    <div className='flex' style={{ width: "199%" }}>
                                                                        <div id={'text' + chat.whatsappId} className="w-[230px] font-semibold line-clamp-2 overflow-hidden" style={{ fontSize: "12px", color: "gray" }}>
                                                                            {(() => {
                                                                                const mt = chat?.lastMessageType || chat?.messageType;
                                                                                const t = chat?.text;
                                                                                if (!mt || mt === 'text') return t;
                                                                                const icon =
                                                                                    mt === 'audio' ? '🎵' :
                                                                                        mt === 'image' ? '📷' :
                                                                                            mt === 'video' ? '🎬' :
                                                                                                mt === 'document' ? '📄' :
                                                                                                    mt === 'broadcast' ? '📣' : '📎';
                                                                                const label =
                                                                                    mt === 'audio' ? 'New Audio Received' :
                                                                                        mt === 'image' ? 'New Image Received' :
                                                                                            mt === 'video' ? 'New Video Received' :
                                                                                                mt === 'document' ? 'New Document Received' :
                                                                                                    mt === 'broadcast' ? 'Broadcast' : 'New Media Received';
                                                                                return `${icon} ${t || label}`;
                                                                            })()}
                                                                        </div>
                                                                        <div className='flex align-bottom' style={{ fontSize: "10px", color: "gray", width: "50px" }}>{formatChatMessageTime(chat.lastMessageDate || parseFloat(chat.lastUpdated))}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    ))}
                                                </AnimatePresence>
                                                :
                                                <div className='m-1.5 italic flex justify-center' style={{ width: "300px", height: "80px" }}>
                                                    {searchQuery ? 'No matching chats' : activeTab === 'unread' ? 'No unread chats' : 'No chats found'}
                                                </div>
                                            }
                                        </motion.div>
                                    </AnimatePresence>
                            }


                        </div>

                    </div>

                    {/* right pane */}
                    <Card
                        title={chatlist?.find(c => c.id === selectedChat)?.name ? (
                            <div className='flex items-center justify-between gap-2 p-2'>
                                <div className='flex items-center gap-2'>
                                    <img style={{ width: "40px", height: "40px", borderRadius: "20px" }} src={person_avatar} alt="DP" loading="lazy" />
                                    <div>
                                        <div className='font-semibold'>
                                            {chatlist?.find(c => c.id === selectedChat)?.name}
                                        </div>
                                        <div className='text-xs text-gray-500'>
                                            {chatlist?.find(c => c.id === selectedChat)?.whatsappId}
                                        </div>
                                    </div>
                                </div>
                                <div className='flex gap-2'>
                                    <Tooltip title="Edit contact name">
                                        <Button size="small" icon={<EditOutlined />} onClick={openEditModal} />
                                    </Tooltip>
                                    <Tooltip title="Delete contact">
                                        <Button size="small" danger icon={<DeleteOutlined />} onClick={openDeleteModal} />
                                    </Tooltip>
                                </div>
                            </div>
                        ) : <div className='flex gap-2 p-2' style={{ height: "40px" }}>Select a chat</div>}
                        size="small"
                        className="flex-1 flex flex-col overflow-hidden"
                        style={{ backgroundColor: "#cacbcc" }}
                        headStyle={{ backgroundColor: 'white', flex: 'none' }}
                        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}
                    >
                        {selectedChat ? (
                            chatWindowLoading ? (
                                <div className='flex flex-1 justify-center items-center'>
                                    <img style={{ width: "100px" }} src={loading_circle_eclipse} />
                                </div>
                            ) : (
                                <>
                                    {/* 1. SCROLLABLE MESSAGE AREA */}
                                    <div ref={messageContainerRef} className='flex-1 overflow-y-auto p-4' style={{ backgroundColor: "#cacbcc" }}>
                                        <Tooltip title={hasMoreMessages ? 'Load older messages' : 'No older messages'}>
                                            <div
                                                className={`flex justify-center py-2 ${hasMoreMessages && !loadingOldChats ? 'cursor-pointer opacity-100' : 'cursor-not-allowed opacity-40'}`}
                                                onClick={hasMoreMessages && !loadingOldChats ? loadOldChats : undefined}
                                            >
                                                {loadingOldChats
                                                    ? <img src={loading_circle_eclipse} style={{ width: '20px', height: '20px' }} />
                                                    : <PlusCircleOutlined />
                                                }
                                            </div>
                                        </Tooltip>

                                        {selectedChatExchanges.map((chat) => (
                                            chat.eventType === 'ticket' ? (
                                                <div key={chat.id} className='flex justify-center'>
                                                    <Divider className='text-xs'>
                                                        {chat?.eventDescription + ' - ' + formatChatMessageTime(new Date(chat?.created).getTime() / 1000)}
                                                    </Divider>
                                                </div>
                                            ) : (
                                                <div
                                                    key={chat.id}
                                                    ref={el => {
                                                        if (el) messageRefsMap.current[chat.id] = el;
                                                        else delete messageRefsMap.current[chat.id];
                                                    }}
                                                    className='flex w-full'
                                                    style={{ justifyContent: chat.owner || chat?.finalText ? 'flex-end' : 'flex-start' }}
                                                >
                                                    <div className={`whitespace-pre-wrap relative mt-1 mb-1 p-2 max-w-[70%] rounded-lg border shadow-sm transition-all duration-300
                                    ${chat?.owner || chat?.finalText
                                                            ? 'bg-[#dcf8c6] rounded-br-none mr-2 after:border-l-[10px] after:border-l-[#dcf8c6] after:-right-2'
                                                            : 'bg-white rounded-bl-none ml-2 after:border-r-[10px] after:border-r-white after:-left-2'}
                                    ${flashingMessageId === chat.id ? 'ring-2 ring-[#8349e6] bg-[#ede7f6]' : ''}
                                    after:content-[''] after:absolute after:bottom-0 after:w-0 after:height-0 after:border-t-[10px] after:border-t-transparent
                                `}>
                                                        {/* Image message */}
                                                        {chat.type === 'image' && (
                                                            chat.data
                                                                ? <div className='flex justify-center relative mb-1' style={{ overflow: 'visible' }}>
                                                                    <Image
                                                                        style={{ maxHeight: "250px", display: 'block' }}
                                                                        className='rounded'
                                                                        src={chat.data}
                                                                        onLoad={handleMediaLoad}
                                                                    />
                                                                    <div
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleDownload(chat.data_original, `file_${chat.id}.jpg`, chat.id);
                                                                        }}
                                                                        className={`bg-black/70 text-white rounded-full flex items-center justify-center transition-all ${downloadingId === chat.id ? 'cursor-not-allowed opacity-100' : 'cursor-pointer hover:scale-110'}`}
                                                                        style={{
                                                                            position: 'absolute', top: '10px', right: '10px', zIndex: 99,
                                                                            width: '35px', height: '35px', boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                                                            pointerEvents: downloadingId ? 'none' : 'auto'
                                                                        }}
                                                                    >
                                                                        {downloadingId === chat.id ? <LoadingOutlined /> : <DownloadOutlined />}
                                                                    </div>
                                                                </div>
                                                                : <div className='flex justify-center items-center rounded bg-gray-200 animate-pulse mb-1'
                                                                    style={{ width: '200px', height: '150px' }} />
                                                        )}

                                                        {/* Video message */}
                                                        {chat.type === 'video' && (
                                                            chat.data
                                                                ? <div className='relative group flex justify-center mb-1'>
                                                                    <video src={chat.data} className="max-h-[360px] rounded" controls onLoadedData={handleMediaLoad}>
                                                                        Your browser does not support the video tag.
                                                                    </video>
                                                                    <div
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleDownload(chat.data_original, `file_${chat.id}.mp4`, chat.id);
                                                                        }}
                                                                        className={`bg-black/70 text-white rounded-full flex items-center justify-center transition-all ${downloadingId === chat.id ? 'cursor-not-allowed opacity-100' : 'cursor-pointer hover:scale-110'}`}
                                                                        style={{
                                                                            position: 'absolute', top: '10px', right: '10px', zIndex: 99,
                                                                            width: '35px', height: '35px', boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                                                            pointerEvents: downloadingId ? 'none' : 'auto'
                                                                        }}
                                                                    >
                                                                        {downloadingId === chat.id ? <LoadingOutlined /> : <DownloadOutlined />}
                                                                    </div>
                                                                </div>
                                                                : <div className='flex justify-center items-center rounded bg-gray-200 animate-pulse mb-1'
                                                                    style={{ width: '200px', height: '200px' }} />
                                                        )}

                                                        {/* Document message */}
                                                        {chat.type === 'document' && (
                                                            <div className='flex items-center gap-2 mb-1 p-2 rounded bg-white/60 border'>
                                                                <FileOutlined style={{ fontSize: 28, color: '#555' }} />
                                                                <div className='flex-1 text-xs text-gray-700 break-all line-clamp-2'>
                                                                    {chat.text || 'Document'}
                                                                </div>
                                                                <div
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleDownload(chat.data_original, chat.text || `document_${chat.id}`, chat.id);
                                                                    }}
                                                                    className={`bg-black/70 text-white rounded-full flex items-center justify-center transition-all flex-shrink-0 ${downloadingId === chat.id ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
                                                                    style={{ width: '28px', height: '28px' }}
                                                                >
                                                                    {downloadingId === chat.id ? <LoadingOutlined style={{ fontSize: 12 }} /> : <DownloadOutlined style={{ fontSize: 12 }} />}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Audio message */}
                                                        {chat.type === 'audio' && (
                                                            chat.data
                                                                ? <div className='flex items-center gap-2 mb-1'>
                                                                    <audio src={chat.data} controls className="max-w-[240px]" style={{ height: '36px' }} />
                                                                    <div
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleDownload(chat.data_original, `audio_${chat.id}.ogg`, chat.id);
                                                                        }}
                                                                        className={`bg-black/70 text-white rounded-full flex items-center justify-center transition-all flex-shrink-0 ${downloadingId === chat.id ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
                                                                        style={{ width: '28px', height: '28px' }}
                                                                    >
                                                                        {downloadingId === chat.id ? <LoadingOutlined style={{ fontSize: 12 }} /> : <DownloadOutlined style={{ fontSize: 12 }} />}
                                                                    </div>
                                                                </div>
                                                                : <div className='flex items-center gap-2 rounded bg-gray-200 animate-pulse mb-1' style={{ width: '240px', height: '36px' }} />
                                                        )}

                                                        {chat?.replySourceMessage && (
                                                            <div
                                                                className='flex border rounded bg-white/60 mb-2 overflow-hidden cursor-pointer hover:bg-white/90 transition-colors duration-150'
                                                                style={{ minWidth: '180px' }}
                                                                onClick={() => scrollToMessage(chat.replySourceMessage.id)}
                                                            >
                                                                <div style={{ width: "4px", backgroundColor: "#8349e6", flexShrink: 0 }}></div>
                                                                <div className='p-2' style={{ flex: 1, minWidth: 0 }}>
                                                                    <div className='text-[11px] font-semibold text-[#8349e6] mb-0.5'>
                                                                        {chat.replySourceMessage.senderName || 'Quoted message'}
                                                                    </div>
                                                                    <div className='text-xs text-gray-700 line-clamp-3'>
                                                                        {formatWhatsAppText(chat?.replySourceMessage?.text)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {formatWhatsAppText(chat?.text || chat?.finalText)}

                                                        <div className="text-[10px] text-gray-500 text-right mt-1 leading-none">
                                                            {chat?.owner && chat?.operatorName ? chat.operatorName + ' · ' : null}
                                                            {formatChatMessageTime(chat?.timestamp || new Date(chat?.created).getTime() / 1000)}
                                                            {chat.owner && chat.statusString ? <> · {renderStatus(chat.statusString)}</> : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        ))}
                                        <div ref={scrollBottomRef} />
                                    </div>

                                    {/* 2. STICKY FOOTER REPLY BOX */}
                                    <div className='flex-none p-3 bg-white flex items-end gap-2'>
                                        <div className='flex-1 bg-[#f0f2f5] rounded-xl px-4 py-1 border border-transparent focus-within:border-[#7bfc68] transition-all duration-200'>
                                            <textarea
                                                ref={messageInputRef}
                                                rows={1}
                                                value={messageText}
                                                onChange={(e) => setMessageText(e.target.value)}
                                                disabled={isSending}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSendMessage();
                                                    }
                                                }}
                                                placeholder="Type a message..."
                                                className='w-full outline-none resize-none bg-transparent pt-2 text-sm text-gray-700'
                                                style={{ minHeight: '32px', maxHeight: '150px' }}
                                            />
                                            <div className='text-[9px] text-gray-400 text-right pb-1'>
                                                {messageText.length} characters
                                            </div>
                                        </div>

                                        <Button
                                            type="primary"
                                            shape="circle"
                                            icon={isSending ? <LoadingOutlined /> : <SendOutlined />}
                                            onClick={handleSendMessage}
                                            disabled={!messageText.trim() || isSending}
                                            className="flex items-center justify-center shadow-md"
                                            style={{
                                                width: '44px',
                                                height: '44px',
                                                backgroundColor: messageText.trim() ? '#075e54' : '#d1d1d1',
                                                borderColor: 'transparent'
                                            }}
                                        />
                                    </div>
                                </>
                            )
                        ) : (
                            /* PLACEHOLDER WHEN NO CHAT SELECTED */
                            <div className='flex-1 flex flex-col justify-center items-center bg-[#cacbcc]'>
                                <img src={whatsapp_placeholder} className="w-48 opacity-50" />
                                <div className='font-bold text-lg mt-4'>
                                    Select a chat to start messaging
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            {/* Search Filter Modal */}
            <Modal
                title={<span><FilterOutlined style={{ marginRight: 8 }} />Search Filter</span>}
                open={filterModalOpen}
                onCancel={() => setFilterModalOpen(false)}
                centered
                width={340}
                footer={[
                    <Button key="cancel" onClick={() => setFilterModalOpen(false)}>Cancel</Button>,
                    <Button key="apply" type="primary" onClick={() => { setFilterModalOpen(false); if (searchQuery) search_chats(searchQuery, searchFilter); }}>Apply</Button>,
                ]}
            >
                <div className='mb-1 mt-3 text-sm text-gray-500'>Search by</div>
                <Select
                    style={{ width: '100%' }}
                    value={searchFilter}
                    onChange={setSearchFilter}
                    options={[
                        { value: 0, label: 'Name / Number' },
                        { value: 1, label: 'Messages' },
                    ]}
                />
                <div className='mt-2 text-xs text-gray-400'>Press Enter or click the search icon to run the search after changing this setting.</div>
            </Modal>

            {/* Edit Contact Modal */}
            <Modal
                title="Edit Contact Name"
                open={editContactModal}
                onCancel={() => setEditContactModal(false)}
                centered
                maskClosable={false}
                footer={[
                    <Button key="cancel" onClick={() => setEditContactModal(false)}>Cancel</Button>,
                    <Button key="save" type="primary" loading={editContactLoading} icon={<EditOutlined />} onClick={handleEditContact}>Save</Button>,
                ]}
            >
                <div className='mb-1 text-sm text-gray-500'>Contact name</div>
                <Input
                    value={editContactName}
                    onChange={e => setEditContactName(e.target.value)}
                    onPressEnter={handleEditContact}
                    autoFocus
                    placeholder="Enter new name"
                />
            </Modal>

            {/* Delete Contact Modal */}
            <Modal
                title="Delete Contact"
                open={deleteContactModal}
                onCancel={() => setDeleteContactModal(false)}
                centered
                maskClosable={false}
                footer={[
                    <Button key="cancel" onClick={() => setDeleteContactModal(false)}>Cancel</Button>,
                    <Button key="delete" danger loading={deleteContactLoading} icon={<DeleteOutlined />} onClick={handleDeleteContact}>Delete</Button>,
                ]}
            >
                <div className='mb-2 border rounded p-3' style={{ backgroundColor: '#f7f7f7' }}>
                    <div className='font-semibold'>{chatlist?.find(c => c.id === selectedChat)?.name}</div>
                    <div className='text-xs text-gray-500'>{chatlist?.find(c => c.id === selectedChat)?.whatsappId}</div>
                </div>
                <div className='mb-1 text-sm text-gray-500'>Reason for deleting</div>
                <TextArea
                    value={deleteReason}
                    onChange={e => setDeleteReason(e.target.value)}
                    autoFocus
                    placeholder="Enter reason..."
                    autoSize={{ minRows: 3, maxRows: 6 }}
                />
                <div className='mt-2 text-xs text-red-500'>This contact and its chat history will be removed.</div>
            </Modal>
        </>

    );
};

export default WhatsappMessages;
